import axios from 'axios';
import { authCode, confirmPass, login } from './paths';
import { logoutUser, setRefreshedToken } from '../redux/slices/userProfile';
import { addSnackbar } from '../redux/slices/snackbar';
import { store } from '../redux/store';
import { history } from '../App';
import * as Sentry from '@sentry/browser';
import localStorageKeys from '../constants/localStorageKeys';
import { version } from '../../package.json';

const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const PERMISSION_CODE = 'permission_denied';

const { dispatch } = store;

const api = axios.create({
  baseURL: baseApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.response.use(
  (response) => {
    const uiVersion = response.headers?.['x-ui-version'];
    if (uiVersion && uiVersion !== version) {
      window.postMessage({
        key: 'new_version_available',
        value: true,
      }, '*');
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest.url.toString();

    if (!error.response) return Promise.reject(error);

    const { status, data: { code } } = error.response;

    if ((status === FORBIDDEN || status === UNAUTHORIZED) && !originalRequest._retry) {
      if (code === PERMISSION_CODE) {
        history.replace('/');

        return Promise.reject(error);
      }

      if (status === FORBIDDEN && (url.endsWith(login) || url.endsWith(authCode) || url.endsWith(confirmPass))) {
        return Promise.reject(error);
      }

      const refresh_token = store.getState().user.tokens.refresh;
      try {
        const refreshTokenApi = axios.create({
          baseURL: baseApiUrl,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const tokens = await refreshTokenApi.post(`${login}token_refresh/`, {
          refresh: refresh_token,
        });

        dispatch(setRefreshedToken({
          access: tokens.data.access,
          refresh: tokens.data.refresh,
        }));

        originalRequest._retry = true;

        originalRequest.headers.Authorization = `Bearer ${tokens.data.access}`;
        api.defaults.headers.common.Authorization = `Bearer ${tokens.data.access}`;

        return api(originalRequest);
      } catch (err: any) {
        localStorage.removeItem(localStorageKeys.viprToken);
        localStorage.removeItem(localStorageKeys.viprRefreshToken);
        localStorage.removeItem(localStorageKeys.viprWSToken);
        localStorage.removeItem(localStorageKeys.permissions);
        dispatch(logoutUser());
        Sentry.setUser(null);
        // If user is on login page - we don't want to show session message
        if (history.location.pathname !== '/') {
          dispatch(addSnackbar({ message: 'Session expired. Please log in again.' as unknown as string }));
        }
        history.replace('/');
      }
    }
    return Promise.reject(error);
  },
);

export default api;
